<template>
  <v-dialog
    :value="value"
    light
    scrollable
    persistent
  >
    <v-card>
      <v-toolbar
        flat
        dense
        color="grey lighten-4"
        class="text-h6"
      >
        <v-spacer />
        <span :class="warning ? 'primary--text' : 'black--text'">
          {{ sum | currency }}
        </span>
        <v-icon
          small
          :color="warning ? 'primary' : 'grey'"
          class="mx-1"
          v-text="warning ? icons.warning : icons.slash"
        />
        <span class="grey--text">
          {{ totalAmountToPay | currency }}
        </span>
      </v-toolbar>
      <v-divider />
      <v-card-text class="pa-0">
        <v-list-item-group
          v-model="selected"
          multiple
        >
          <template v-for="(content, i) in contents">
            <v-divider v-if="i > 0" />
            <v-list-item
              :key="`item-${i}`"
              :value="i"
              :disabled="content.paid"
              active-class="primary--text"
              class="pl-0"
            >
              <template #default="{ active }">
                <v-list-item-action class="mx-3">
                  <v-checkbox
                    v-if="! content.paid"
                    :input-value="active"
                    color="primary"
                  />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ content.name }}
                    <v-icon
                      v-if="content.menu"
                      x-small
                      :color="content.paid ? 'grey' : 'primary'"
                      v-text="icons.menu"
                    />
                  </v-list-item-title>
                  <div
                    class="text-caption grey--text"
                    v-text="content.extras"
                  />
                </v-list-item-content>
                <v-list-item-action-text
                  :class="{'grey--text text--lighten-1': content.paid}"
                >
                  {{ content.price | currency }}
                </v-list-item-action-text>
              </template>
            </v-list-item>
          </template>
        </v-list-item-group>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-btn
          text
          color="grey darken-1"
          @click="cancel()"
          v-text="__('cancel')"
        />
        <v-spacer />
        <v-btn
          :disabled="warning || selected.length == 0"
          text
          color="primary"
          @click="pay()"
          v-text="__('done')"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import Configurable from '@/Mixins/Configurable'
  import Currenciable from '@/Mixins/Currenciable'
  import I18n from '@/Mixins/I18n'
  import UsesIcons from '@/Mixins/UsesIcons'
  import OrderContentType from '@/Models/Static/OrderContentType'

  export default {
    mixins: [
      Configurable,
      Currenciable,
      I18n,
      UsesIcons,
    ],

    props: {
      value: {
        required: true,
        type: Boolean,
      },
    },

    data: () => ({
      selected: [],
      OrderContentType,
    }),

    computed: {
      order () {
        return this.$store.state.orders.current
      },
      contents () {
        return this.order.orderContents
          ?.flatMap((content, index) => new Array(content.quantity)
            .fill({
              index,
              name: content.name,
              extras: this.formatExtras(content),
              price: content.total / content.quantity,
              menu: content.subContents[0]?.type === this.OrderContentType.MENU,
            })
            .map((o, i) => ({
              ...o,
              paid: Boolean(i >= (content.quantity - content.alreadyPaidQuantity)),
            }))
          )
          .sort((a, b) => a.paid === b.paid ? a.name.localeCompare(b.name) : a.paid ? 1 : -1) ?? [] // REV-5307
      },
      sum () {
        return this.selected.reduce((acc, current) => acc + this.contents[current].price, 0)
      },
      totalAmountToPay () {
        return this.order.total - this.order.alreadyPaid
      },
      warning () {
        return Number(this.sum).greaterThan(this.totalAmountToPay)
      },
    },

    methods: {
      formatExtras (content) {
        if (content.modifiers?.length) {
          return content.modifiers.pluck('name').join(', ')
        }
        if (! content.subContents?.length || this.config('hideMenuContents') || content.quantity > 1) {
          return ''
        }
        return content.getFormattedSubContents(false)
          .map(subContent => {
            let str = `${subContent.quantity} ${subContent.itemName}`
            if (subContent.modifiers?.length) {
              str += ' (' + subContent.modifiers.pluck('name').join(', ') + ')'
            }
            return str
          })
          .join(', ')
      },
      cancel () {
        this.selected = []
        this.$emit('input', false)
        this.$store.dispatch('orders/payment/reset')
      },
      pay () {
        this.$emit('input', false)
        this.$store.commit('orders/payment/payPerItems/indexes', this.selected.map(i => this.contents[i].index))
        this.$store.dispatch('orders/payment/payingPerItems', this.sum)
        setTimeout(() => this.selected = [], 100)
      },
    },
  }
</script>
